import {Inject, Injectable} from '@angular/core';
import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import {environment, CustomEnvironment, setApiOverrides} from '../../environments/environment';
import {firstValueFrom, Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import * as _ from 'lodash';
import {HttpErrorHandlerService, HandleError} from './http-error-handling.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': 'my-auth-token'
  })
};

const queryString = (data: any) => Object.keys(data).filter(f => data[f]).map((k) => k + '=' + data[k]).join('&');

@Injectable()
export class FilesService {
  private handleError: HandleError;
  public nameToIdentityMap = {
    'tonto-application': {
      parse: x => parseInt(x, 10),
      key: 'tontoApplicationId',
    },
    'tonto-applicant': {
      parse: x => parseInt(x, 10),
      key: 'tontoApplicantId',
    },
    'justice-application': {
      parse: x => parseInt(x, 10),
      key: 'justiceApplicationId',
    },
    'justice-applicant': {
      parse: x => parseInt(x, 10),
      key: 'justiceApplicantId',
    },
    'v8-application': {
      parse: x => parseInt(x, 10),
      key: 'v8ApplicationId',
    },
    'v8-applicant': {
      parse: x => parseInt(x, 10),
      key: 'v8ApplicantId',
    },
    'crumbs-party': {
      parse: x => parseInt(x, 10),
      key: 'crumbsPartyId',
    },
    'crumbs-party-role': {
      parse: x => parseInt(x, 10),
      key: 'crumbsPartyRoleId',
    },
    'vx-application': {
      parse: x => parseInt(x, 10),
      key: 'vxApplicationId',
    },
    'vx-applicant': {
      parse: x => parseInt(x, 10),
      key: 'vxApplicantId',
    },
    'collateral': {
      parse: x => parseInt(x, 10),
      key: 'collateralId',
    },
    'condition': {
      parse: x => parseInt(x, 10),
      key: 'conditionId',
    },
    adhoc: {
      parse: x => x,
      key: 'adhoc',
    },
  };

  constructor(private oauthService: OAuthService,
              private http: HttpClient,
              httpErrorHandler: HttpErrorHandlerService,
              @Inject(CustomEnvironment) customEnvironment: any) {
    setApiOverrides(customEnvironment);

    this.oauthService.events.subscribe((event: OAuthEvent) => {
      const tokenUpdatedEvents = ['token_refreshed', 'token_received', 'silently_refreshed'];
      if (tokenUpdatedEvents.includes(event.type)) {
        httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
      }
    });

    httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
    this.handleError = httpErrorHandler.createHandleError('FilesService');
  }

  getFiles(id: number, identityType: any, tid: number, docCodes: string, pageNumber: number, pageSize: number, sort = 'typeCode', sortDirection = 1): Observable<any> {
    const data: any = {
      id,
      idt: identityType,
      tid,
      dcs: docCodes,
      pn: pageNumber,
      ps: pageSize,
      s: sort,
      sd: sortDirection
    };
    const params = queryString(data);

    return this.http.get<any[]>(`${environment.api.files}/files?${params}`, httpOptions);
  }

  getFileByIdentity(id: number, idt: string) {
    return this.http.get(`${environment.api.files}/files?id=${id}&idt=${idt}`, httpOptions);
  }

  uploadFile(data) {
    return this.http.post(`${environment.api.files}/files`, data, httpOptions);
  }

  deployToArchive(data) {
    return this.http.post(`${environment.api.files}/files/deploy-to-archive`, data, httpOptions);
  }

  /**
   * Make URL for file upload (does NOT cater for workflow)
   * @param data
   */
  async makeUrlDirect(data: any): Promise<any> {
    try {
      const result = this.http.post(`${environment.api.files}/make-url/direct`, data, httpOptions);
      return await firstValueFrom(result);
    } catch (error) {
      console.error('Error in makeUrlDirect:', error);
      throw error;
    }
  }

  /**
   * Make URL for file upload (caters for workflow)
   * @param data
   */
  async makeUrlProcessor(data: any): Promise<any> {
    try {
      const result = this.http.post(`${environment.api.files}/make-url/processor`, data, httpOptions);
      return await firstValueFrom(result);
    } catch (error) {
      console.error('Error in makeUrlProcessor:', error);
      throw error;
    }
  }

  /**
   * File upload with make url
   * @param fileData
   */
  async uploadFileWithUrl(fileData: any) {
    try {
      const metadata: any = _.clone(fileData);
      delete metadata.data;

      const {id, url} = fileData.workflow ? await this.makeUrlProcessor(metadata) : await this.makeUrlDirect(metadata);
      const awsHttpOptions = {
        headers: new HttpHeaders({
          'content-type': fileData.contentType,
        })
      };

      await firstValueFrom(this.http.put(url, fileData.data, awsHttpOptions));

      return id;
    } catch (error) {
      console.error('Error in uploadFileWithUrl:', error);
      throw error;
    }
  }

  downloadBlob(presignedUrl: string): Observable<Blob> {
    return this.http.get(presignedUrl, { responseType: 'blob' });
  }

  downloadBinary(presignedUrl: string): Observable<string> {
    return this.http.get(presignedUrl, { responseType: 'arraybuffer' }).pipe(
      map(arrayBuffer => this.arrayBufferToBase64(arrayBuffer))
    );
  }

  arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  async downloadFile(file: any, download = true) {
    try {
      const presignedUrl: string = await this.getUrlForDownload(file._id);

      if (!download) {
        return firstValueFrom(this.downloadBinary(presignedUrl));
      }

      this.downloadBlob(presignedUrl).subscribe(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = file.name;
        link.click();
      });

    } catch (error) {
      console.log('Could not download file:', error);
      throw error;
    }
  }

  async getUrlForDownload(id: string): Promise<any> {
    try {
      return await firstValueFrom(this.http.get(`${environment.api.files}/${id}/make-url`, httpOptions));
    } catch (error) {
      console.error('Error in downloadFile:', error);
      throw error;
    }
  }

  addIdentity(id: string, idt: string, idv: any, data: any) {
    return this.http.post(`${environment.api.files}/files/${id}/identities/${idt}/${idv}`, JSON.stringify(data), httpOptions);
  }

  setVisibility(id, data) {
    return this.http.put(`${environment.api.files}/files/${id}/visibility`, data, httpOptions);
  }

  downloadFileAsBinary(id: string) {
    return this.http.get(`${environment.api.files}/files/${id}/binary`, httpOptions)
      .pipe(
        catchError(this.handleError('downloadFile', []))
      );
  }

  updateDocType(id: string, data) {
    return this.http.put(`${environment.api.files}/files/${id}/doc-type`, data, httpOptions)
      .pipe(
        catchError(this.handleError('uploadFile', []))
      );
  }

  getFileContentType(id: string) {
    return this.http.get(`${environment.api.files}/files/${id}/content-type`, httpOptions)
      .pipe(
        catchError(this.handleError('getFileContentType', []))
      );
  }

  // remove file
  deleteFile(id: string): Observable<any> {
    return this.http.delete(`${environment.api.files}/files/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('deleteFile', []))
      );
  }

  mergeFiles(data) {
    return this.http.post(`${environment.api.files}/files/merge-pdf`, data, httpOptions)
      .pipe(
        catchError(this.handleError('mergeFiles', []))
      );
  }

  mergeAndSaveFiles(data) {
    return this.http.post(`${environment.api.files}/files/merge-pdf/save`, data, httpOptions)
      .pipe(
        catchError(this.handleError('mergeFiles', []))
      );
  }

  getFile(id: string) {
    return this.http.get(`${environment.api.files}/files/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('getFile', []))
      );
  }

  getMapper(v) {
    return this.nameToIdentityMap[v];
  }

  getDocumentCategoriesBySystem(system = 'vx', includeDocTypes = false) {
    const url = `${environment.api.files}/system/${system}/document-categories?includeDocTypes=${includeDocTypes.toString()}`;
    return this.http.get(url, httpOptions);
  }

  searchFilesCount(data): Observable<any> {
    const payload: any = this.cleanPayload(data);
    return this.http.post(`${environment.api.files}/files-search/count`, payload, httpOptions);
  }

  getFilesCount(id: number, identityType: any, tid: number, documentCategory: string): Observable<any> {
    const data: any = {
      id,
      idt: identityType,
      tid,
      dcs: documentCategory
    };
    const params = queryString(data);

    return this.http.get<any[]>(`${environment.api.files}/files/count?${params}`, httpOptions);
  }

  searchFiles(data): Observable<any> {
    const payload: any = this.cleanPayload(data);
    return this.http.post(`${environment.api.files}/files-search`, payload, httpOptions);
  }

  cleanPayload(payload: any) {
    const cleanedPayload = {};

    for (const key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const value = payload[key];

        // Check if the value is not an empty array or undefined
        if (Array.isArray(value)) {
          if (value.length > 0) {
            cleanedPayload[key] = value;
          }
        } else if (value !== null && value !== undefined && value !== '') {
          cleanedPayload[key] = value;
        }
      }
    }

    return cleanedPayload;
  }
}
